import { LogLevel } from '@loomhq/enums';

import { isDevOrTest } from 'constants/environment';
import { CLIENT_LOG_LEVEL } from 'constants/runtimeConfig';

import * as tracker from './sentry';

export const initTracker = () => {
  return tracker.init();
};

const ALL_LEVELS = Object.values(LogLevel);

let LOG_LEVEL = CLIENT_LOG_LEVEL || '';
const validateLogLevel = level => level && ALL_LEVELS.includes(level);
const isAllowed = level => {
  if (!validateLogLevel(level)) {
    return false;
  }

  const allowedLevels = LOG_LEVEL.replace(/\s/g, '').split(',');

  return allowedLevels.includes(level);
};

export const setLogLevel = (level = '') => {
  const levels = level.replace(/\s/g, '').split(',').filter(validateLogLevel);

  if (!levels.length) {
    throw `Invalid levels, levels allowed: ${ALL_LEVELS}`;
  }

  LOG_LEVEL = levels.join(',');

  return levels;
};

const logConsole = (level, ...args) => {
  // eslint-disable-next-line
  console[level](...args.filter(Boolean));
};

export const fatal = (exception, context, tags) => {
  if (!isAllowed(LogLevel.Fatal)) {
    return;
  }

  if (isDevOrTest) {
    return logConsole(LogLevel.Error, exception, context, tags);
  }

  logConsole(LogLevel.Error, exception.message || exception);
  tracker.logFatal(exception, context, { priority: 'p0', ...tags });
};

export const error = (exception, context, tags) => {
  if (!isAllowed(LogLevel.Error)) {
    return;
  }

  if (isDevOrTest) {
    return logConsole(LogLevel.Error, exception, context, tags);
  }

  logConsole(LogLevel.Error, exception.message || exception);
  tracker.logError(exception, context, { priority: 'p1', ...tags });
};

export const warning = (exception, context) => {
  if (!isAllowed(LogLevel.Warning)) {
    return;
  }

  if (isDevOrTest) {
    return logConsole(LogLevel.Warning, exception, context);
  }

  logConsole(LogLevel.Warning, exception.message || exception);
};

const logToConsole = (level, message, context) => {
  if (!isAllowed(level)) {
    return;
  }

  logConsole(level, message, context);
};

export const info = (message, context) => {
  logToConsole(LogLevel.Info, message, context);
};
export const debug = (message, context) => {
  logToConsole(LogLevel.Debug, message, context);
};

export const addCrumb = ({
  message = '',
  context = {},
  additionalProps = {},
}) => {
  return tracker.addBreadcrumb({
    message,
    data: context,
    ...additionalProps,
    timestamp: Date.now(),
  });
};

export const captureCrumb = (categoryName, crumb) => {
  const [timestamp, details] = crumb;

  tracker.addBreadcrumb({
    category: categoryName,
    timestamp,
    ...details,
  });

  const { message, level, context } = details;

  const friendly = () => [message, context];
  const serious = () => [new Error(message), context];

  const handlers = {
    [LogLevel.Debug]: [debug, friendly],
    [LogLevel.Info]: [info, friendly],
    [LogLevel.Warning]: [warning, serious],
    [LogLevel.Error]: [error, serious],
    [LogLevel.Fatal]: [fatal, serious],
  };

  const [fn, argsFn] = handlers[level];

  fn(...argsFn());
};

window.setLoomDebuggingLevel = setLogLevel;

export const COMPANY_TEAM = 'company';
export const INTEGRATE_TEAM = 'integrate';
export const LIFECYCLE_TEAM = 'lifecycle';
export const BILLING_TEAM = 'billing';
export const CREATE_TEAM = 'create';
